import React from 'react';
import { useQuery } from 'react-query';

import { Select as AntDSelect, AutoComplete, Form } from 'antd';
import styled from 'styled-components';

import { getCities } from 'apis/CommunityAPI';
import Input from 'components/lib/Input';
import Select from 'components/lib/Select';
import TextArea from 'components/lib/TextArea';
import { CANADIAN_PROVINCES, US_STATES } from 'helpers/constants';
import { normalizeCommunityWebsiteUrl } from 'helpers/urls';

const { Option } = AntDSelect;

const Row = styled.div`
  display: flex;
  gap: 10px;
`;

const StyledTextArea = styled(TextArea)`
  resize: vertical;
  margin: 2px 0;
`;

const StateOptionHeader = styled.span`
  font-size: 14px;
  font-family: var(--font-bold);
  display: flex;
  justify-content: start;
`;

export const StyledAutoComplete = styled(AutoComplete)`
  .ant-select-selector {
    border-radius: 0px !important;
    height: 36px !important;
    box-shadow: none !important;
    background-color: var(--background-gray) !important;
    display: flex;
    align-items: center;
  }

  .ant-select-selection-search-input {
    height: 100% !important;
  }
  .ant-select-selection-placeholder {
    color: var(--text-secondary) !important;
  }
`;

const BasicInfoEditFormGeneral = () => {
  const form = Form.useFormInstance();
  const { data: cities } = useQuery({
    queryKey: ['cities', 'basic', 'info'],
    queryFn: () => getCities(),
    refetchOnWindowFocus: false,
  });

  return (
    <>
      <Form.Item name="address" label="Address" rules={[{ required: true, message: 'Enter Community Address' }]}>
        <Input style={{ height: 36 }} placeholder={'Enter Address'} />
      </Form.Item>
      <Form.Item name="city" label="City" rules={[{ required: true, message: 'Enter City' }]}>
        <StyledAutoComplete
          showSearch
          className={'rectangular'}
          optionFilterProp="children"
          filterOption={(input, option) =>
            ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={(cities ?? [])
            .filter((item: string) => !!item)
            .map((item: string) => ({ value: item, label: item }))}
          placeholder="Select City"
          dropdownStyle={{
            padding: 0,
            borderRadius: 0,
            border: '1px solid var(--line-gray)',
            boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
          }}
          style={{ height: '36px' }}
        />
      </Form.Item>
      <Row>
        <Form.Item
          name="state"
          label="State & ZIP Code"
          rules={[
            { required: true, message: 'Select State' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const validOptions = [...US_STATES, ...CANADIAN_PROVINCES].map((o) => o.value);
                if (!value || validOptions.includes(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Invalid state selected'));
              },
            }),
          ]}
        >
          <Select
            showSearch
            className={'rectangular'}
            filterOption={(input, option) =>
              (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={[
              { label: 'US States', options: US_STATES },
              { label: 'Canadian Province/Territory', options: CANADIAN_PROVINCES },
            ]}
            placeholder="Select State"
            style={{ height: '36px', minWidth: '120px', maxWidth: '220px' }}
            dropdownStyle={{
              padding: 0,
              borderRadius: 0,
              border: '1px solid var(--line-gray)',
              boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
            }}
          />
        </Form.Item>
        <Form.Item name="zip_code" rules={[{ required: true, message: 'Enter ZIP code' }]}>
          <Input style={{ width: 120, height: 36 }} placeholder={'Enter ZIP code'} />
        </Form.Item>
      </Row>
      <Form.Item name="website" label="Website">
        <Input
          style={{ height: 36 }}
          placeholder={'Enter Website (optional)'}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            form.setFieldValue('website', normalizeCommunityWebsiteUrl(event.target.value));
          }}
        />
      </Form.Item>
      <Form.Item name="notes" label="Community Notes">
        <StyledTextArea rows={3} placeholder="Enter a note" />
      </Form.Item>
      <Form.Item name="year_opened" label="Year Opened">
        <Input style={{ width: 120, height: 36 }} placeholder={'Enter Year (optional)'} />
      </Form.Item>
    </>
  );
};

export default BasicInfoEditFormGeneral;
